import { useCallback, useEffect, useState } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  GitMerge,
  Sparkles,
} from "lucide-react";
import {
  adminListProductos,
  adminCreateProducto,
  adminUpdateProducto,
  adminDeleteProducto,
  adminMergeProductos,
  adminSeedExampleProductos,
  type AdminProducto,
  type GameTypeAll,
  type ProductoNivel,
  type CreateProductoBody,
} from "../../lib/api";
import { useAdminCtx } from "./AdminLayout";
import { useDialogs } from "../../hooks/useDialogs";
import { useUndoableDelete } from "../../hooks/useUndoableDelete";
import { Modal } from "../../components/ui/Modal";
import { NumberField } from "../../components/admin/forms/NumberField";
import { HelpPopover } from "../../components/admin/forms/HelpPopover";

// Página de productos unificados (modelo nuevo migration 016+020).
// Reemplaza a /admin/premios — un producto es 1 fila con stock único
// que participa en 0-N juegos (ruleta/raspadita/memoria) con pesos
// diferentes por juego.
//
// Diseño: tabla compacta con columnas (Nombre, Categoría, Stock,
// Nivel, Juegos, Activo, Acciones). Modal para crear/editar.
// Acciones inline: Editar / Borrar / Iniciar fusión.

type Categoria = "real" | "consolation" | "reintento";
const GAME_TYPES: GameTypeAll[] = ["ruleta", "raspadita", "memoria"];
const GAME_LABELS: Record<GameTypeAll, string> = {
  ruleta: "Ruleta",
  raspadita: "Raspadita",
  memoria: "Memoria",
};
const NIVELES: ProductoNivel[] = ["facil", "medio", "dificil"];
const NIVEL_LABELS: Record<ProductoNivel, string> = {
  facil: "Fácil",
  medio: "Medio",
  dificil: "Difícil",
};

function categoriaDe(p: AdminProducto): Categoria {
  if (p.is_reintento === 1) return "reintento";
  if (p.is_consolation === 1) return "consolation";
  return "real";
}

export default function ProductosPage() {
  const { sucursalIdEfectiva } = useAdminCtx();
  const dialogs = useDialogs();
  const undoDelete = useUndoableDelete();
  const [productos, setProductos] = useState<AdminProducto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminProducto | "new" | null>(null);
  // Reorg UX 2026-06: la fusion paso de un modo global de 2 pasos
  // implicito (seleccionar origen en una fila, despues tocar "fusionar
  // aqui" en otra) a un modal explicito "Fusionar [origen] dentro de
  // [destino]". Mas claro y sin riesgo de fusionar el producto equivocado.
  const [mergeOpen, setMergeOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await adminListProductos(sucursalIdEfectiva);
      setProductos(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [sucursalIdEfectiva]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Toggle Activo inline con update optimista. Reorg UX 2026-06:
  // activar/desactivar un premio (lo mas frecuente, ej se agoto stock)
  // pasaba por abrir el modal full-form. Ahora es 1 click en la celda.
  async function handleToggleActivo(p: AdminProducto) {
    const nuevo = p.activo === 1 ? 0 : 1;
    setProductos((prev) =>
      prev.map((x) => (x.id === p.id ? { ...x, activo: nuevo } : x)),
    );
    try {
      await adminUpdateProducto(
        p.id,
        { activo: nuevo === 1 },
        sucursalIdEfectiva,
      );
    } catch (e) {
      // Revertir el optimismo si el server rechaza.
      setProductos((prev) =>
        prev.map((x) => (x.id === p.id ? { ...x, activo: p.activo } : x)),
      );
      await dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
    }
  }

  // Edicion inline del stock con update optimista (reorg UX 2026-06):
  // ajustar el stock de un premio pasa de abrir el modal full-form a
  // editar la celda directo.
  async function handleUpdateStock(p: AdminProducto, nuevo: number | null) {
    setProductos((prev) =>
      prev.map((x) => (x.id === p.id ? { ...x, stock: nuevo } : x)),
    );
    try {
      await adminUpdateProducto(p.id, { stock: nuevo }, sucursalIdEfectiva);
    } catch (e) {
      setProductos((prev) =>
        prev.map((x) => (x.id === p.id ? { ...x, stock: p.stock } : x)),
      );
      dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
    }
  }

  // Edicion inline del peso por juego (reorg UX 2026-06). El peso vive
  // en producto_por_juego; el PATCH reemplaza el array completo de juegos,
  // asi que reenviamos todos los juegos con el peso modificado.
  async function handleUpdatePeso(
    p: AdminProducto,
    juegoType: GameTypeAll,
    nuevoPeso: number,
  ) {
    const nuevosJuegos = p.juegos.map((j) =>
      j.juego_type === juegoType ? { ...j, peso: nuevoPeso } : j,
    );
    setProductos((prev) =>
      prev.map((x) => (x.id === p.id ? { ...x, juegos: nuevosJuegos } : x)),
    );
    try {
      await adminUpdateProducto(
        p.id,
        {
          juegos: nuevosJuegos.map((j) => ({
            juego_type: j.juego_type,
            peso: j.peso,
            sort_order: j.sort_order,
          })),
        },
        sucursalIdEfectiva,
      );
    } catch (e) {
      setProductos((prev) => prev.map((x) => (x.id === p.id ? p : x)));
      dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
    }
  }

  // Borrado con Deshacer (5s) en vez de modal bloqueante — el undo es la
  // red de seguridad. El DELETE real al server se difiere hasta que vence
  // la ventana (ver useUndoableDelete).
  function handleDelete(p: AdminProducto) {
    undoDelete({
      key: p.id,
      message: `"${p.nombre}" borrado`,
      removeOptimistic: () =>
        setProductos((prev) => prev.filter((x) => x.id !== p.id)),
      restore: () =>
        setProductos((prev) =>
          prev.some((x) => x.id === p.id) ? prev : [...prev, p],
        ),
      commit: () => adminDeleteProducto(p.id, sucursalIdEfectiva),
      onError: (e) => {
        dialogs.alert({
          message: e instanceof Error ? e.message : String(e),
          variant: "error",
        });
        refetch();
      },
    });
  }

  async function handleSeedExamples() {
    const ok = await dialogs.confirm({
      title: "Cargar productos de ejemplo",
      message:
        "Se van a crear ~10 productos típicos de shopping (café, vouchers, cine, consolation, etc.) en esta sucursal. Los nombres que ya existan se saltean. Podés editarlos o borrarlos después como cualquier producto. ¿Continuás?",
      confirmLabel: "Cargar ejemplos",
    });
    if (!ok) return;
    setSeeding(true);
    try {
      const res = await adminSeedExampleProductos(sucursalIdEfectiva);
      await dialogs.alert({
        message:
          `Listo: ${res.created} producto${res.created === 1 ? "" : "s"} creado${res.created === 1 ? "" : "s"}` +
          (res.skipped > 0
            ? `, ${res.skipped} salteado${res.skipped === 1 ? "" : "s"} (ya existían).`
            : "."),
        variant: "success",
      });
      refetch();
    } catch (e) {
      await dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
    } finally {
      setSeeding(false);
    }
  }

  async function handleMerge(srcId: number, dstId: number) {
    if (srcId === dstId) return;
    const src = productos.find((p) => p.id === srcId);
    const dst = productos.find((p) => p.id === dstId);
    const ok = await dialogs.confirm({
      title: "Fusionar productos",
      message:
        `¿Fusionar "${src?.nombre}" dentro de "${dst?.nombre}"?\n\n` +
        `"${src?.nombre}" DESAPARECE; sus juegos y plays se mueven a "${dst?.nombre}". Esta acción no se puede deshacer.`,
      confirmLabel: "Fusionar",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await adminMergeProductos(srcId, dstId, sucursalIdEfectiva);
      setMergeOpen(false);
      refetch();
    } catch (e) {
      await dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
    }
  }

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin opacity-50" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="sr-only">
            Premios
            <HelpPopover>
              Lista unificada. Cada premio es 1 fila con stock único, puede
              participar en uno o varios juegos con pesos diferentes.
            </HelpPopover>
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {productos.length >= 2 && (
            <button
              type="button"
              onClick={() => setMergeOpen(true)}
              className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium transition hover:bg-white/10"
              title="Fusionar dos productos duplicados en uno"
            >
              <GitMerge className="h-4 w-4 text-amber-300" />
              Fusionar
            </button>
          )}
          <button
            type="button"
            onClick={handleSeedExamples}
            disabled={seeding}
            className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium transition hover:bg-white/10 disabled:opacity-50"
            title="Cargar una lista curada de productos típicos para arrancar rápido"
          >
            {seeding ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 text-[#a78bfa]" />
            )}
            Cargar ejemplos
          </button>
          <button
            type="button"
            onClick={() => setEditing("new")}
            data-tour="productos-new"
            className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] px-4 py-2 font-bold transition hover:scale-[1.02]"
          >
            <Plus className="h-4 w-4" />
            Nuevo producto
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-500/15 px-4 py-2 text-sm text-red-300">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {productos.length === 0 ? (
        <div className="flex flex-col items-center gap-4 rounded-lg bg-white/5 px-6 py-12 text-center">
          <p className="opacity-70">
            No hay productos cargados.
          </p>
          <button
            type="button"
            onClick={handleSeedExamples}
            disabled={seeding}
            className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] px-5 py-2.5 font-bold transition hover:scale-[1.02] disabled:opacity-50"
          >
            {seeding ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Cargar productos de ejemplo
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white/5" data-tour="productos-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider opacity-60">
                <th className="px-4 py-2">Nombre</th>
                <th className="px-4 py-2">Categoría</th>
                <th className="px-4 py-2">Stock</th>
                <th className="px-4 py-2">Nivel</th>
                <th className="px-4 py-2">Juegos</th>
                <th className="px-4 py-2">Activo</th>
                <th className="px-4 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {productos.map((p) => (
                <ProductoRow
                  key={p.id}
                  producto={p}
                  onEdit={() => setEditing(p)}
                  onDelete={() => handleDelete(p)}
                  onToggleActivo={() => handleToggleActivo(p)}
                  onUpdateStock={(s) => handleUpdateStock(p, s)}
                  onUpdatePeso={(jt, peso) => handleUpdatePeso(p, jt, peso)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <ProductoModal
          producto={editing === "new" ? null : editing}
          sucursalIdEfectiva={sucursalIdEfectiva}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refetch();
          }}
        />
      )}

      {mergeOpen && (
        <MergeModal
          productos={productos}
          onClose={() => setMergeOpen(false)}
          onMerge={handleMerge}
        />
      )}
    </div>
  );
}

function ProductoRow({
  producto,
  onEdit,
  onDelete,
  onToggleActivo,
  onUpdateStock,
  onUpdatePeso,
}: {
  producto: AdminProducto;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActivo: () => void;
  onUpdateStock: (nuevo: number | null) => void;
  onUpdatePeso: (juegoType: GameTypeAll, nuevoPeso: number) => void;
}) {
  const cat = categoriaDe(producto);
  // Edicion inline del peso por chip de juego.
  const [editPeso, setEditPeso] = useState<GameTypeAll | null>(null);
  // Edicion inline del stock (reorg UX 2026-06): click en la celda →
  // input; Enter/blur guarda, Escape cancela. Vacio = sin limite (∞).
  const [editStock, setEditStock] = useState(false);
  const [stockDraft, setStockDraft] = useState("");

  function commitStock() {
    setEditStock(false);
    const trimmed = stockDraft.trim();
    const nuevo = trimmed === "" ? null : Math.max(0, Math.floor(Number(trimmed)));
    if (trimmed !== "" && Number.isNaN(Number(trimmed))) return; // basura → cancelar
    if (nuevo === producto.stock) return;
    onUpdateStock(nuevo);
  }
  // Keys internas ("real" / "consolation" / "reintento") se mantienen
  // porque mapean a flags is_consolation/is_reintento del backend y al
  // audit log histórico. Lo que cambia es solo el label visible: "Real"
  // → "Premio" y "Consolation" → "Sin-premio" (más claro en español).
  const catLabel: Record<Categoria, string> = {
    real: "Premio",
    consolation: "Sin-premio",
    reintento: "Reintento",
  };
  const catColor: Record<Categoria, string> = {
    real: "bg-emerald-500/15 text-emerald-300",
    consolation: "bg-amber-500/15 text-amber-300",
    reintento: "bg-blue-500/15 text-blue-300",
  };
  return (
    <tr className="border-b border-white/5 transition hover:bg-white/5">
      <td className="px-4 py-2">
        <div className="flex items-center gap-2">
          {producto.color && (
            <div
              className="h-3 w-3 shrink-0 rounded-full border border-white/20"
              style={{ background: producto.color }}
            />
          )}
          <span className="font-medium">{producto.nombre}</span>
        </div>
      </td>
      <td className="px-4 py-2">
        <span className={`inline-block rounded px-2 py-0.5 text-xs ${catColor[cat]}`}>
          {catLabel[cat]}
        </span>
      </td>
      <td className="px-4 py-2">
        {editStock ? (
          <input
            type="number"
            min={0}
            autoFocus
            defaultValue={producto.stock === null ? "" : producto.stock}
            onChange={(e) => setStockDraft(e.target.value)}
            onBlur={commitStock}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitStock();
              } else if (e.key === "Escape") {
                setEditStock(false);
              }
            }}
            placeholder="∞"
            className="w-20 rounded bg-white/10 px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-[#a78bfa]"
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setStockDraft(producto.stock === null ? "" : String(producto.stock));
              setEditStock(true);
            }}
            title="Click para editar el stock"
            className="rounded px-1.5 py-0.5 text-left hover:bg-white/10"
          >
            {producto.stock === null ? (
              <span className="opacity-50 italic">∞</span>
            ) : producto.stock === 0 ? (
              <span className="text-red-400">0 (agotado)</span>
            ) : (
              producto.stock
            )}
          </button>
        )}
      </td>
      <td className="px-4 py-2">
        {producto.nivel ? (
          <span className="rounded bg-white/10 px-2 py-0.5 text-xs">
            {NIVEL_LABELS[producto.nivel]}
          </span>
        ) : (
          <span className="opacity-30">—</span>
        )}
      </td>
      <td className="px-4 py-2">
        <div className="flex flex-wrap gap-1">
          {producto.juegos.length === 0 && (
            <span className="text-xs opacity-30 italic">Sin juego</span>
          )}
          {producto.juegos.map((j) =>
            editPeso === j.juego_type ? (
              <span
                key={j.juego_type}
                className="flex items-center gap-1 rounded bg-[#a78bfa]/15 px-1.5 py-0.5 text-[10px] text-[#c4b5fd]"
              >
                {GAME_LABELS[j.juego_type]} · p
                <input
                  type="number"
                  min={0}
                  autoFocus
                  defaultValue={j.peso}
                  onBlur={(e) => {
                    setEditPeso(null);
                    // Campo vacio o basura -> cancelar (no commitear peso 0).
                    const raw = e.target.value.trim();
                    if (raw === "" || Number.isNaN(Number(raw))) return;
                    const v = Math.max(0, Math.floor(Number(raw)));
                    if (v !== j.peso) onUpdatePeso(j.juego_type, v);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      (e.target as HTMLInputElement).blur();
                    } else if (e.key === "Escape") {
                      setEditPeso(null);
                    }
                  }}
                  className="w-10 rounded bg-white/15 px-1 text-[10px] outline-none focus:ring-1 focus:ring-[#a78bfa]"
                />
              </span>
            ) : (
              <button
                type="button"
                key={j.juego_type}
                onClick={() => setEditPeso(j.juego_type)}
                title={`peso ${j.peso} · orden ${j.sort_order} — click para editar el peso`}
                className="rounded bg-[#a78bfa]/15 px-1.5 py-0.5 text-[10px] text-[#c4b5fd] hover:bg-[#a78bfa]/30"
              >
                {GAME_LABELS[j.juego_type]} · p{j.peso}
              </button>
            ),
          )}
        </div>
      </td>
      <td className="px-4 py-2">
        {/* Toggle inline: 1 click activa/desactiva sin abrir el modal. */}
        <button
          type="button"
          onClick={onToggleActivo}
          title={producto.activo === 1 ? "Activo — click para desactivar" : "Inactivo — click para activar"}
          aria-label={producto.activo === 1 ? "Desactivar producto" : "Activar producto"}
          aria-pressed={producto.activo === 1}
          className={`relative h-5 w-9 shrink-0 rounded-full transition ${
            producto.activo === 1 ? "bg-emerald-500/70" : "bg-white/15"
          }`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
              producto.activo === 1 ? "left-[18px]" : "left-0.5"
            }`}
          />
        </button>
      </td>
      <td className="px-4 py-2 text-right">
        <div className="flex justify-end gap-1">
          <button
            type="button"
            onClick={onEdit}
            className="rounded p-1.5 opacity-70 hover:bg-white/10 hover:opacity-100"
            title="Editar"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="rounded p-1.5 text-red-300 hover:bg-red-500/15"
            title="Borrar"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ──────────────────────────────────────────────
// Modal de fusión — reorg UX 2026-06
// ──────────────────────────────────────────────
// Origen y destino elegidos explicitamente en 2 selects, en una sola
// pantalla. Reemplaza el modo global de 2 pasos que mutaba la UI de
// todas las filas y daba lugar a fusionar el producto equivocado.
function MergeModal({
  productos,
  onClose,
  onMerge,
}: {
  productos: AdminProducto[];
  onClose: () => void;
  onMerge: (srcId: number, dstId: number) => void;
}) {
  const [srcId, setSrcId] = useState<number | "">("");
  const [dstId, setDstId] = useState<number | "">("");
  const mismatch = srcId !== "" && dstId !== "" && srcId === dstId;
  const listo = srcId !== "" && dstId !== "" && !mismatch;

  return (
    <Modal open onClose={onClose} closeOnBackdrop>
      <div className="flex flex-col gap-4">
        <h2 className="text-2xl font-bold">Fusionar productos</h2>
        <p className="text-sm opacity-70">
          El <strong>origen</strong> desaparece dentro del{" "}
          <strong>destino</strong>.
        </p>

        <label className="flex flex-col gap-1 text-sm">
          <span className="opacity-80">Origen (desaparece)</span>
          <select
            value={srcId}
            onChange={(e) =>
              setSrcId(e.target.value === "" ? "" : Number(e.target.value))
            }
            className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
          >
            <option value="">Elegí un producto…</option>
            {productos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="opacity-80">Destino (se queda)</span>
          <select
            value={dstId}
            onChange={(e) =>
              setDstId(e.target.value === "" ? "" : Number(e.target.value))
            }
            className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
          >
            <option value="">Elegí un producto…</option>
            {productos.map((p) => (
              <option key={p.id} value={p.id} disabled={p.id === srcId}>
                {p.nombre}
              </option>
            ))}
          </select>
        </label>

        {mismatch && (
          <div className="rounded-lg bg-amber-500/15 px-3 py-2 text-sm text-amber-200">
            Origen y destino tienen que ser productos distintos.
          </div>
        )}

        <div className="mt-2 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg bg-white/10 py-3 font-bold hover:bg-white/20"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!listo}
            onClick={() => listo && onMerge(Number(srcId), Number(dstId))}
            className="flex-1 rounded-lg bg-gradient-to-r from-amber-500 to-orange-600 py-3 font-bold disabled:opacity-50"
          >
            Fusionar
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ──────────────────────────────────────────────
// Modal de crear/editar producto
// ──────────────────────────────────────────────
function ProductoModal({
  producto,
  sucursalIdEfectiva,
  onClose,
  onSaved,
}: {
  producto: AdminProducto | null;
  sucursalIdEfectiva: number | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = producto === null;

  const [nombre, setNombre] = useState(producto?.nombre ?? "");
  const [color, setColor] = useState(producto?.color ?? "");
  const [categoria, setCategoria] = useState<Categoria>(
    producto ? categoriaDe(producto) : "real",
  );
  // Stock: null = sin tracking. number = trackeado.
  const [stockEnabled, setStockEnabled] = useState(producto?.stock !== null);
  const [stockValue, setStockValue] = useState(
    producto?.stock !== null && producto?.stock !== undefined ? producto.stock : 10,
  );
  const [nivel, setNivel] = useState<ProductoNivel | "">(producto?.nivel ?? "");
  const [activo, setActivo] = useState(producto ? producto.activo === 1 : true);
  // Estado de juegos: para cada juego, si participa + peso + sort_order.
  const [juegos, setJuegos] = useState<
    Record<GameTypeAll, { enabled: boolean; peso: number; sort_order: number }>
  >(() => {
    const base: Record<GameTypeAll, { enabled: boolean; peso: number; sort_order: number }> = {
      ruleta: { enabled: false, peso: 1, sort_order: 0 },
      raspadita: { enabled: false, peso: 1, sort_order: 0 },
      memoria: { enabled: false, peso: 1, sort_order: 0 },
    };
    producto?.juegos.forEach((j) => {
      base[j.juego_type] = { enabled: true, peso: j.peso, sort_order: j.sort_order };
    });
    return base;
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const body: CreateProductoBody = {
        nombre: nombre.trim(),
        color: color.trim() || null,
        is_consolation: categoria === "consolation",
        is_reintento: categoria === "reintento",
        activo,
        stock: stockEnabled ? stockValue : null,
        nivel: nivel === "" ? null : nivel,
        juegos: GAME_TYPES.filter((g) => juegos[g].enabled).map((g) => ({
          juego_type: g,
          peso: juegos[g].peso,
          sort_order: juegos[g].sort_order,
        })),
      };
      if (isNew) {
        await adminCreateProducto(body, sucursalIdEfectiva);
      } else {
        await adminUpdateProducto(producto.id, body, sucursalIdEfectiva);
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      closeOnBackdrop
      maxWidthClass="max-w-2xl"
      labelledBy="producto-modal-title"
    >
      <div className="mb-4 flex items-start justify-between">
        <h2
          id="producto-modal-title"
          className="font-heading text-2xl font-bold"
        >
          {isNew ? "Nuevo producto" : "Editar producto"}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 opacity-60 hover:bg-white/10 hover:opacity-100"
          aria-label="Cerrar"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Nombre + color */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[2fr_1fr]">
            <label className="flex flex-col gap-1 text-sm">
              <span className="opacity-80">Nombre *</span>
              <input
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
                className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="opacity-80">Color (hex)</span>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={color || "#000000"}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-10 w-12 cursor-pointer rounded bg-white/10"
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="#abc123"
                  className="flex-1 rounded-lg bg-white/10 px-3 py-2 outline-none"
                />
              </div>
            </label>
          </div>

          {/* Categoría */}
          <div className="flex flex-col gap-2 text-sm">
            <span className="opacity-80">Categoría</span>
            <div className="grid grid-cols-3 gap-2">
              {(["real", "consolation", "reintento"] as Categoria[]).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCategoria(c)}
                  className={`rounded-lg border-2 px-3 py-2 text-sm transition ${
                    categoria === c
                      ? "border-[#a78bfa] bg-[#a78bfa]/10"
                      : "border-white/10 hover:bg-white/5"
                  }`}
                >
                  {c === "real" ? "Premio" : c === "consolation" ? "Sin-premio" : "Reintento"}
                </button>
              ))}
            </div>
          </div>

          {/* Stock */}
          <div className="flex flex-col gap-2 rounded-lg bg-white/5 p-3 text-sm">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={stockEnabled}
                onChange={(e) => setStockEnabled(e.target.checked)}
                className="h-4 w-4"
              />
              <span>Limitar stock</span>
            </label>
            {stockEnabled && (
              <NumberField
                value={stockValue}
                onChange={setStockValue}
                min={0}
                step={1}
                ariaLabel="Stock"
                className="w-32 rounded-lg bg-white/10 px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#a78bfa]"
              />
            )}
            {!stockEnabled && (
              <span className="flex items-center gap-1 text-xs opacity-60">
                Sin límite
                <HelpPopover>
                  El producto siempre se ofrece, sin tope de stock.
                </HelpPopover>
              </span>
            )}
          </div>

          {/* Nivel */}
          <label className="flex flex-col gap-1 text-sm">
            <span className="flex items-center gap-1 opacity-80">
              Nivel
              <HelpPopover>
                Opcional. Lo usa el juego Memoria para agrupar productos por
                dificultad.
              </HelpPopover>
            </span>
            <select
              value={nivel}
              onChange={(e) => setNivel(e.target.value as ProductoNivel | "")}
              className="rounded-lg bg-white/10 px-3 py-2 outline-none"
            >
              <option value="">Sin nivel</option>
              {NIVELES.map((n) => (
                <option key={n} value={n}>
                  {NIVEL_LABELS[n]}
                </option>
              ))}
            </select>
          </label>

          {/* Activo */}
          <label className="flex cursor-pointer items-center gap-2 rounded-lg bg-white/5 px-3 py-2 text-sm">
            <input
              type="checkbox"
              checked={activo}
              onChange={(e) => setActivo(e.target.checked)}
              className="h-4 w-4"
            />
            <span>Activo (visible en los juegos)</span>
          </label>

          {/* Juegos */}
          <div className="rounded-lg bg-white/5 p-4">
            <div className="mb-3 text-sm font-medium opacity-80">
              Participa en
            </div>
            <div className="flex flex-col gap-3">
              {GAME_TYPES.map((g) => (
                <div key={g} className="flex flex-wrap items-center gap-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={juegos[g].enabled}
                      onChange={(e) =>
                        setJuegos((prev) => ({
                          ...prev,
                          [g]: { ...prev[g], enabled: e.target.checked },
                        }))
                      }
                      className="h-4 w-4"
                    />
                    <span className="w-20">{GAME_LABELS[g]}</span>
                  </label>
                  {juegos[g].enabled && (
                    <>
                      <label className="flex items-center gap-1 text-xs opacity-80">
                        <span>Peso</span>
                        <NumberField
                          value={juegos[g].peso}
                          onChange={(v) =>
                            setJuegos((prev) => ({
                              ...prev,
                              [g]: { ...prev[g], peso: v },
                            }))
                          }
                          min={1}
                          step={1}
                          ariaLabel={`Peso en ${GAME_LABELS[g]}`}
                          className="w-16 rounded bg-white/10 px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-[#a78bfa]"
                        />
                      </label>
                      <label className="flex items-center gap-1 text-xs opacity-80">
                        <span>Orden</span>
                        <NumberField
                          value={juegos[g].sort_order}
                          onChange={(v) =>
                            setJuegos((prev) => ({
                              ...prev,
                              [g]: { ...prev[g], sort_order: v },
                            }))
                          }
                          min={0}
                          step={1}
                          ariaLabel={`Orden en ${GAME_LABELS[g]}`}
                          className="w-16 rounded bg-white/10 px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-[#a78bfa]"
                        />
                      </label>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-white/10 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm opacity-70 hover:bg-white/5 hover:opacity-100"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || nombre.trim().length === 0}
              className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] px-4 py-2 font-bold disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isNew ? "Crear" : "Guardar"}
            </button>
          </div>
        </form>
    </Modal>
  );
}
